import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || '';

// Format a 2-decimal price string (MXN) as "$300.00".
function formatPrice(price) {
  const amount = Number(price);
  if (Number.isNaN(amount)) return price;
  return `$${amount.toFixed(2)}`;
}

// Strip accent marks for the menu headings and item names. The 'Mounties'
// display font draws letters as capitals and renders accented vowels as
// mismatched lowercase glyphs, which looks wrong (e.g. "CAMARóN", "ATúN").
// Display-only — the source data in DynamoDB is untouched.
function stripAccents(text) {
  return String(text).normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

// Small coral wave/tilde flourish used to flank the "MENÚ" word.
function Flourish({ flip = false }) {
  return (
    <svg
      className={`menu-flourish${flip ? ' flip' : ''}`}
      viewBox="0 0 60 16"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M2 10c6-9 12-9 17 0s11 9 17 0 11-9 17 0"
        fill="none"
        stroke="var(--coral)"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

// Coral sprig/branch decoration for the left margin.
function CoralSprig() {
  return (
    <svg
      className="menu-sprig"
      viewBox="0 0 60 120"
      aria-hidden="true"
      focusable="false"
    >
      <g fill="none" stroke="var(--coral)" strokeWidth="3" strokeLinecap="round">
        <path d="M30 118V30" />
        <path d="M30 92c-10-4-16-14-16-26" />
        <path d="M30 74c10-4 16-14 16-26" />
        <path d="M30 56c-9-3-13-11-13-21" />
        <path d="M30 40c8-3 12-10 12-19" />
        <path d="M30 30c0-10 5-18 12-22" />
        <path d="M30 30c0-10-5-18-12-22" />
      </g>
    </svg>
  );
}

export default function Menu() {
  const [categories, setCategories] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ready | error

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setStatus('loading');
      try {
        const response = await fetch(`${API_URL}/api/menu`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        if (cancelled) return;
        setCategories(Array.isArray(data.categories) ? data.categories : []);
        setStatus('ready');
      } catch (err) {
        if (cancelled) return;
        setStatus('error');
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const hasItems = categories.some((c) => c.items && c.items.length > 0);

  return (
    <section className="menu-section">
      <div className="menu-sheet">
        <header className="menu-header">
          <img
            className="menu-logo"
            src="/images/coral-logo.png"
            alt="Coral Reef & Beef"
          />
          <h1 className="menu-title">
            <Flourish />
            <span>Menu</span>
            <Flourish flip />
          </h1>
        </header>

        {status === 'loading' && (
          <p className="menu-state" aria-live="polite">Cargando el menú…</p>
        )}

        {status === 'error' && (
          <div className="menu-state" role="alert">
            <p>No pudimos cargar el menú en este momento.</p>
            <Link className="reserve" to="/">Volver al inicio <span>←</span></Link>
          </div>
        )}

        {status === 'ready' && !hasItems && (
          <p className="menu-state">El menú estará disponible muy pronto.</p>
        )}

        {status === 'ready' && hasItems && (
          <div className="menu-categories">
            <CoralSprig />
            {categories
              .filter((c) => c.items && c.items.length > 0)
              .map((category) => (
                <div className="menu-category" key={category.category}>
                  <h2 className="menu-category-title">
                    <span className="menu-category-swash" aria-hidden="true" />
                    <span className="menu-category-text">{stripAccents(category.category)}</span>
                  </h2>
                  <ul className="menu-items">
                    {category.items.map((item) => (
                      <li className="menu-item" key={item.id}>
                        <div className="menu-item-row">
                          <span className="menu-item-name">{stripAccents(item.name)}</span>
                          <span className="menu-item-leader" aria-hidden="true" />
                          <span className="menu-item-price">{formatPrice(item.price)}</span>
                        </div>
                        {item.description ? (
                          <p className="menu-item-desc">{item.description}</p>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
          </div>
        )}

        <svg
          className="menu-waves"
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M0 70c80-30 160-30 240 0s160 30 240 0 160-30 240 0 160 30 240 0 160-30 240 0v50H0z"
            fill="var(--ink)"
            opacity="0.18"
          />
          <path
            d="M0 86c80-26 160-26 240 0s160 26 240 0 160-26 240 0 160 26 240 0 160-26 240 0v34H0z"
            fill="var(--ink)"
            opacity="0.3"
          />
          <path
            d="M0 100c80-20 160-20 240 0s160 20 240 0 160-20 240 0 160 20 240 0 160-20 240 0v20H0z"
            fill="var(--ink)"
            opacity="0.5"
          />
        </svg>

        <footer className="menu-footer">
          <span className="menu-footer-rule" aria-hidden="true" />
          <em>Precios en MXN</em>
          <span className="menu-footer-rule" aria-hidden="true" />
        </footer>
      </div>
    </section>
  );
}
